import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  GET_FAMILY,
  GET_CUSTODY_EVENTS,
  UPSERT_CUSTODY_EVENT,
  UPDATE_CUSTODY_EVENT,
} from '@common/graphql';
import {
  CustodyDate,
  CustodyPlan,
  Household,
  CustodyPlanWithId,
  FamilyHouseholdData,
} from '../types';
import {
  getCustodyDates,
  custodyPlanToState,
  getInitialCustodyState,
} from '../utils/custody';

interface CustodyEventRow {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  data: {
    custodyPlan?: CustodyPlan;
    households?: Record<string, Household>;
    swapDates?: Record<string, string>;
  };
  families: { family_id: string }[];
}

function isNewFormatEvent(event: CustodyEventRow): boolean {
  return !!event.data?.custodyPlan;
}

interface UseCustodyEventsResult {
  custodyDates: Record<string, CustodyDate>;
  households: Record<string, Household>;
  custodyPlan: CustodyPlan | null;
  custodyPlans: CustodyPlanWithId[];
  existingEventId: string | null;
  loading: boolean;
  error: any;
  familyHouseholdData: FamilyHouseholdData | undefined;
  saveCustodySchedule: (
    custodyDates: Record<string, CustodyDate>,
    familyId: string,
    userId: string,
    plan?: CustodyPlan,
    planHouseholds?: Record<string, Household>,
    updateEventId?: string | null
  ) => Promise<void>;
  closePlanAtDate: (planId: string, endDate: Date) => Promise<void>;
  refetch: () => void;
}

export function useCustodyEvents(): UseCustodyEventsResult {
  const { data: familyQueryData, loading: familyLoading } = useQuery(GET_FAMILY);
  const families = (familyQueryData as any)?.family || [];
  const familyId = families[0]?.id;
  const familyHouseholdData: FamilyHouseholdData | undefined = families[0]?.data;

  // Note: family_id filtering is handled by Hasura permissions
  const { data: queryData, loading: queryLoading, error, refetch } = useQuery(
    GET_CUSTODY_EVENTS,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  // Extract existing event ID (for updating instead of recreating)
  const existingEventId = useMemo<string | null>(() => {
    const events: CustodyEventRow[] = (queryData as any)?.event || [];
    if (events.length === 0) return null;

    const firstEvent = events[0];
    if (isNewFormatEvent(firstEvent)) {
      return firstEvent.id;
    }

    return null;
  }, [queryData]);

  // Extract all custody plans from events
  const custodyPlans = useMemo<CustodyPlanWithId[]>(() => {
    const events: CustodyEventRow[] = (queryData as any)?.event || [];
    const plans: CustodyPlanWithId[] = [];

    for (const event of events) {
      if (isNewFormatEvent(event) && event.data.custodyPlan) {
        plans.push({
          id: event.id,
          plan: event.data.custodyPlan,
          households: event.data.households || {},
        });
      }
    }

    // Sort by start date descending (newest first)
    plans.sort((a, b) => b.plan.startDate - a.plan.startDate);
    return plans;
  }, [queryData]);

  // Extract first custody plan
  const custodyPlan = useMemo<CustodyPlan | null>(() => {
    const events: CustodyEventRow[] = (queryData as any)?.event || [];
    if (events.length === 0) return null;

    const firstEvent = events[0];
    if (isNewFormatEvent(firstEvent)) {
      return firstEvent.data.custodyPlan || null;
    }

    return null;
  }, [queryData]);

  // Extract households from new format or family data
  const households = useMemo<Record<string, Household>>(() => {
    const events: CustodyEventRow[] = (queryData as any)?.event || [];

    // First try to get from custody events
    if (events.length > 0) {
      const firstEvent = events[0];
      if (isNewFormatEvent(firstEvent) && firstEvent.data.households) {
        return firstEvent.data.households;
      }
    }

    // Fallback to family data
    if (familyHouseholdData?.households) {
      return familyHouseholdData.households;
    }

    return {};
  }, [queryData, familyHouseholdData]);

  // Calculate custody dates from plans
  const custodyDates = useMemo<Record<string, CustodyDate>>(() => {
    const events: CustodyEventRow[] = (queryData as any)?.event || [];

    if (events.length === 0) {
      return {};
    }

    const firstEvent = events[0];

    if (isNewFormatEvent(firstEvent) && firstEvent.data.custodyPlan && firstEvent.data.households) {
      const planData = firstEvent.data.custodyPlan;
      const planHouseholds = firstEvent.data.households;

      const partialState = custodyPlanToState(planData);
      const fullState = { ...getInitialCustodyState(), ...partialState };

      try {
        const result = getCustodyDates(fullState, planHouseholds);
        return result.items;
      } catch (err) {
        console.error('[useCustodyEvents] Error expanding custody dates:', err);
        return {};
      }
    }

    return {};
  }, [queryData]);

  const [upsertCustodyEvent] = useMutation(UPSERT_CUSTODY_EVENT);
  const [updateCustodyEvent] = useMutation(UPDATE_CUSTODY_EVENT);

  // Close an existing plan at a specific date
  const closePlanAtDate = useCallback(
    async (planId: string, endDate: Date) => {
      const planToClose = custodyPlans.find((p) => p.id === planId);
      if (!planToClose) {
        console.error('[closePlanAtDate] Plan not found:', planId);
        return;
      }

      const updatedPlan = {
        ...planToClose.plan,
        endDate: endDate.getTime(),
      };

      const eventData = {
        custodyPlan: updatedPlan,
        households: planToClose.households,
        swapDates: updatedPlan.swapDates || {},
      };

      try {
        await updateCustodyEvent({
          variables: {
            id: planId,
            start_time: new Date(updatedPlan.startDate).toISOString(),
            end_time: endDate.toISOString(),
            data: eventData,
          },
        });
        await refetch();
      } catch (err) {
        console.error('[closePlanAtDate] Error closing plan:', err);
        throw err;
      }
    },
    [custodyPlans, updateCustodyEvent, refetch]
  );

  const saveCustodySchedule = useCallback(
    async (
      dates: Record<string, CustodyDate>,
      fId: string,
      userId: string,
      plan?: CustodyPlan,
      planHouseholds?: Record<string, Household>,
      updateEventId?: string | null
    ) => {
      if (!plan || !planHouseholds) {
        console.warn('[saveCustodySchedule] No plan provided');
        return;
      }

      const startDate = new Date(plan.startDate);
      const endDate = new Date(plan.endDate);

      const eventData = {
        custodyPlan: plan,
        households: planHouseholds,
        swapDates: plan.swapDates || {},
      };

      try {
        if (updateEventId) {
          await updateCustodyEvent({
            variables: {
              id: updateEventId,
              start_time: startDate.toISOString(),
              end_time: endDate.toISOString(),
              data: eventData,
            },
          });
        } else {
          const object = {
            title: 'Custody Schedule',
            calendar: 'Custody',
            is_all_day: true,
            start_time: startDate.toISOString(),
            end_time: endDate.toISOString(),
            created_by: userId,
            data: eventData,
            families: {
              data: [{ family_id: fId }],
            },
          };

          await upsertCustodyEvent({ variables: { object } });
        }

        await refetch();
      } catch (err) {
        console.error('[saveCustodySchedule] Error saving custody schedule:', err);
        throw err;
      }
    },
    [upsertCustodyEvent, updateCustodyEvent, refetch]
  );

  return {
    custodyDates,
    households,
    custodyPlan,
    custodyPlans,
    existingEventId,
    loading: familyLoading || queryLoading,
    error,
    familyHouseholdData,
    saveCustodySchedule,
    closePlanAtDate,
    refetch,
  };
}

export default useCustodyEvents;
